import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Calendar,
  DollarSign,
  Receipt,
  Users,
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  RefreshCw,
  ChevronRight,
  X,
  CreditCard,
  Smartphone,
  Banknote,
  Lock,
  Unlock,
  Printer,
  CalendarDays,
  CalendarRange,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';
import DateFilter from '../components/DateFilter';

export default function DailyClosing() {
  const { user } = useAuth();
  const isCashier = user?.role === 'cashier';

  // Selected date defaults to today (YYYY-MM-DD)
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [closingData, setClosingData] = useState({
    date: selectedDate,
    summary: {
      total_sales: 0,
      total_invoices: 0,
      total_cashiers: 0,
      closed_cashiers: 0,
      pending_closings: 0,
    },
    cashiers: [],
  });

  const [selectedCashier, setSelectedCashier] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadDailyClosing();
  }, [selectedDate]);

  const loadDailyClosing = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/daily-closings?date=${selectedDate}`);
      const data = res?.data || res;
      if (data) {
        setClosingData({
          date: data.date || selectedDate,
          summary: data.summary || {
            total_sales: 0,
            total_invoices: 0,
            total_cashiers: 0,
            closed_cashiers: 0,
            pending_closings: 0,
          },
          cashiers: Array.isArray(data.cashiers) ? data.cashiers : [],
        });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load daily closing data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminCloseForCashier = async (cashier) => {
    if (!cashier) return;
    setActionLoading(true);
    try {
      const payload = {
        business_date: selectedDate,
        staff_id: cashier.staff_id,
        notes: `Shift closed by Admin (${user?.full_name || 'Admin'})`,
      };
      await api.post('/daily-closings/close', payload);
      toast.success(`Closed shift for ${cashier.staff_name} successfully!`);
      await loadDailyClosing();
      // Update selected cashier in modal
      setSelectedCashier((prev) => prev ? { ...prev, status: 'CLOSED', closed_at: new Date().toISOString() } : null);
    } catch (err) {
      toast.error(err.message || 'Failed to close shift for cashier');
    } finally {
      setActionLoading(false);
    }
  };

  const formatSelectedDateHeading = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-').map(Number);
      const dateObj = new Date(y, m - 1, d);
      return formatPakistanDate(dateObj, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatTimeOnly = (dtStr) => {
    if (!dtStr) return '—';
    try {
      const dt = new Date(dtStr);
      return dt.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dtStr;
    }
  };

  if (isCashier) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500 mb-3" />
          <h2 className="text-xl font-bold text-gray-900">Admin Access Only</h2>
          <p className="mt-2 text-sm text-gray-600">
            The full Daily Closing register is restricted to Shop Owners and Admins.
            You can close and view your own daily sales from the POS or Sales History page.
          </p>
        </div>
      </div>
    );
  }

  const { summary, cashiers } = closingData;
  const formattedDateTitle = formatSelectedDateHeading(selectedDate);

  return (
    <div className="space-y-6 pb-12">
      {/* ── Closing Tabs Toggle (Daily vs Monthly) ── */}
      <div className="flex items-center gap-1.5 border-b border-gray-200 pb-3">
        <Link
          to="/daily-closing"
          className="flex items-center gap-2 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200/80 px-4 py-2 text-sm font-bold shadow-xs transition"
        >
          <CalendarDays className="h-4 w-4 text-emerald-600" />
          <span>Daily Closing</span>
          <span className="rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-extrabold uppercase">
            Active
          </span>
        </Link>
        <Link
          to="/monthly-closing"
          className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition"
        >
          <CalendarRange className="h-4 w-4" />
          <span>Monthly Closing</span>
        </Link>
      </div>

      {/* ── Page Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-md shadow-emerald-500/20">
              <CalendarDays className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">Daily Closing</h1>
              <p className="text-xs text-gray-500 font-medium">
                Monitor and verify cashier shift closings & daily register status
              </p>
            </div>
          </div>
        </div>

        {/* Date Selector Controls */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                setSelectedDate(today);
              }}
              className={clsx(
                "px-3 py-1.5 text-xs font-bold rounded-lg transition-colors",
                selectedDate === new Date().toISOString().slice(0, 10)
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const yest = new Date();
                yest.setDate(yest.getDate() - 1);
                setSelectedDate(yest.toISOString().slice(0, 10));
              }}
              className={clsx(
                "px-3 py-1.5 text-xs font-bold rounded-lg transition-colors",
                (() => {
                  const y = new Date();
                  y.setDate(y.getDate() - 1);
                  return selectedDate === y.toISOString().slice(0, 10);
                })()
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Yesterday
            </button>
          </div>

          <div className="relative">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-semibold text-gray-800 shadow-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <button
            type="button"
            onClick={loadDailyClosing}
            disabled={loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-emerald-600 disabled:opacity-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={clsx("h-4 w-4", loading && "animate-spin text-emerald-600")} />
          </button>
        </div>
      </div>

      {/* ── Selected Date Banner ── */}
      <div className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-emerald-800 via-emerald-700 to-teal-800 px-6 py-4 text-white shadow-lg shadow-emerald-900/10">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-200 block">
            Selected Business Date
          </span>
          <span className="text-xl font-extrabold tracking-tight">
            {formattedDateTitle}
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-2 rounded-xl bg-white/10 px-3.5 py-1.5 backdrop-blur-md text-xs font-medium text-emerald-100">
          <Calendar className="h-4 w-4 text-emerald-300" />
          <span>Showing Cashiers & Register Closings</span>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Sales */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-emerald-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Total Sales</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100">
              <DollarSign className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-gray-900 tracking-tight">
              Rs. {Number(summary.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">All cashiers combined</p>
          </div>
        </div>

        {/* Total Invoices */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-indigo-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Total Invoices</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
              <Receipt className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-gray-900 tracking-tight">
              {summary.total_invoices || 0}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">Orders processed today</p>
          </div>
        </div>

        {/* Closed Cashiers */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-teal-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Closed Cashiers</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600 transition-colors group-hover:bg-teal-100">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-black text-emerald-700 tracking-tight">
              {summary.closed_cashiers || 0} / {summary.total_cashiers || 0}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">Closings finalized</p>
          </div>
        </div>

        {/* Pending Closings */}
        <div className="group rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-amber-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Pending Closings</span>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 transition-colors group-hover:bg-amber-100">
              <Clock className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-3">
            <span className={clsx(
              "text-2xl font-black tracking-tight",
              summary.pending_closings > 0 ? "text-amber-600" : "text-gray-900"
            )}>
              {summary.pending_closings || 0}
            </span>
            <p className="mt-1 text-xs text-gray-500 font-medium">Shifts currently open</p>
          </div>
        </div>
      </div>

      {/* ── Cashier Closing Table ── */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between bg-gray-50/50">
          <div>
            <h3 className="text-base font-bold text-gray-900">Cashier Closing Status</h3>
            <p className="text-xs text-gray-500">
              Click on any cashier's row to inspect detailed payment breakdown & closing records
            </p>
          </div>
          <span className="text-xs font-bold text-gray-500 bg-white border border-gray-200 px-3 py-1 rounded-full shadow-xs">
            {cashiers.length} {cashiers.length === 1 ? 'Cashier / Staff' : 'Cashiers / Staff'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-6 py-3.5">Cashier</th>
                <th className="px-6 py-3.5">Role</th>
                <th className="px-6 py-3.5 text-center">Invoices</th>
                <th className="px-6 py-3.5 text-right">Total Sales</th>
                <th className="px-6 py-3.5 text-center">Closed At</th>
                <th className="px-6 py-3.5 text-center">Status</th>
                <th className="px-6 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent mb-2" />
                    <p className="text-xs font-medium">Loading cashier closing records...</p>
                  </td>
                </tr>
              ) : cashiers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                    <AlertCircle className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                    <p className="text-sm font-semibold text-gray-600">No sales or cashier activity found</p>
                    <p className="text-xs text-gray-400 mt-1">
                      No invoices were recorded on {formattedDateTitle}.
                    </p>
                  </td>
                </tr>
              ) : (
                cashiers.map((cashier) => {
                  const isClosed = cashier.status === 'CLOSED';
                  return (
                    <tr
                      key={cashier.staff_id}
                      onClick={() => setSelectedCashier(cashier)}
                      className="cursor-pointer transition-colors hover:bg-emerald-50/40 group"
                    >
                      {/* Cashier Name */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            "flex h-9 w-9 items-center justify-center rounded-xl font-bold text-xs uppercase shadow-xs",
                            isClosed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                          )}>
                            {cashier.staff_name?.charAt(0) || 'C'}
                          </div>
                          <div>
                            <span className="font-bold text-gray-900 block group-hover:text-emerald-700 transition-colors">
                              {cashier.staff_name}
                            </span>
                            {cashier.username && (
                              <span className="text-xs text-gray-400">@{cashier.username}</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="px-6 py-4">
                        <span className={clsx(
                          "inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-bold uppercase tracking-wider",
                          cashier.role?.toUpperCase() === 'ADMIN'
                            ? "bg-purple-100 text-purple-700"
                            : cashier.role?.toUpperCase() === 'PHARMACIST'
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        )}>
                          {cashier.role}
                        </span>
                      </td>

                      {/* Invoices */}
                      <td className="px-6 py-4 text-center font-bold text-gray-800">
                        {cashier.invoice_count}
                      </td>

                      {/* Total Sales */}
                      <td className="px-6 py-4 text-right font-black text-gray-900">
                        Rs. {Number(cashier.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* Closed At */}
                      <td className="px-6 py-4 text-center text-xs font-medium text-gray-600">
                        {cashier.closed_at ? formatTimeOnly(cashier.closed_at) : '—'}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 text-center">
                        <span className={clsx(
                          "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold tracking-wide uppercase shadow-xs",
                          isClosed
                            ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                            : "bg-amber-100 text-amber-700 border border-amber-200 animate-pulse"
                        )}>
                          {isClosed ? (
                            <>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              CLOSED
                            </>
                          ) : (
                            <>
                              <Clock className="h-3.5 w-3.5" />
                              OPEN
                            </>
                          )}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 group-hover:text-emerald-700">
                          <span>Details</span>
                          <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Cashier Closing Details Modal ── */}
      {selectedCashier && (
        <CashierDetailModal
          cashier={selectedCashier}
          dateTitle={formattedDateTitle}
          onClose={() => setSelectedCashier(null)}
          onAdminClose={() => handleAdminCloseForCashier(selectedCashier)}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
}

function CashierDetailModal({ cashier, dateTitle, onClose, onAdminClose, actionLoading }) {
  const isClosed = cashier.status === 'CLOSED';

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg max-h-[88vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-100 animate-scale-in">
        {/* Header (Compact) */}
        <div className={clsx(
          "px-5 py-3 flex-shrink-0 flex items-center justify-between text-white transition-colors",
          isClosed ? "bg-gradient-to-r from-emerald-700 to-teal-800" : "bg-gradient-to-r from-amber-600 to-orange-700"
        )}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 backdrop-blur-md">
              {isClosed ? <CheckCircle2 className="h-4 w-4 text-white" /> : <Clock className="h-4 w-4 text-white" />}
            </div>
            <div>
              <h3 className="text-base font-bold leading-tight">Cashier Closing Details</h3>
              <p className="text-[11px] text-white/80 font-medium">
                {cashier.staff_name} • {cashier.role}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body (Scrollable & Compact) */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1 text-xs">
          
          {/* Key Info Banner */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-2.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                Cashier Name
              </span>
              <span className="text-sm font-extrabold text-gray-900 block truncate">
                {cashier.staff_name}
              </span>
              <span className="text-[11px] text-gray-500">Role: {cashier.role}</span>
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-2.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                Business Date
              </span>
              <span className="text-sm font-extrabold text-gray-900 block truncate">
                {dateTitle}
              </span>
              <span className="text-[11px] text-gray-500">
                Invoices: <strong className="text-gray-900">{cashier.invoice_count}</strong>
              </span>
            </div>
          </div>

          {/* Status & Closed Time */}
          <div className={clsx(
            "rounded-lg p-2.5 flex items-start gap-2.5 border",
            isClosed
              ? "bg-emerald-50/80 border-emerald-200 text-emerald-900"
              : "bg-amber-50/80 border-amber-200 text-amber-900"
          )}>
            {isClosed ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            ) : (
              <Clock className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="text-[11px] leading-snug flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-bold">Status:</span>
                <span className={clsx(
                  "font-extrabold uppercase px-1.5 py-0.5 rounded text-[10px]",
                  isClosed ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"
                )}>
                  {cashier.status}
                </span>
              </div>
              <p className="mt-0.5 text-gray-700">
                {isClosed ? (
                  <>Closed At: <strong className="text-emerald-950 font-bold">{formatPakistanDateTime(cashier.closed_at)}</strong></>
                ) : (
                  <>This cashier has not clicked <em>Close My Sales</em> yet.</>
                )}
              </p>
              {cashier.closed_by_name && isClosed && (
                <p className="text-[10px] text-emerald-700 mt-0.5">
                  Verified by: {cashier.closed_by_name}
                </p>
              )}
            </div>
          </div>

          {/* Sales Breakdown */}
          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
            <div className="flex items-center justify-between pb-1.5 border-b border-gray-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Total Sales</span>
              <span className="text-lg font-black text-emerald-700">
                Rs. {Number(cashier.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            <div className="space-y-1.5 text-xs pt-0.5">
              <div className="flex items-center justify-between py-0.5 border-b border-gray-50">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Banknote className="h-3.5 w-3.5 text-blue-500" />
                  <span>Cash Sales</span>
                </div>
                <span className="font-bold text-gray-900">
                  Rs. {Number(cashier.cash_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              <div className="flex items-center justify-between py-0.5 border-b border-gray-50">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <CreditCard className="h-3.5 w-3.5 text-purple-500" />
                  <span>Card Sales</span>
                </div>
                <span className="font-bold text-gray-900">
                  Rs. {Number(cashier.card_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              <div className="flex items-center justify-between py-0.5 border-b border-gray-50">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Smartphone className="h-3.5 w-3.5 text-cyan-500" />
                  <span>Online Sales</span>
                </div>
                <span className="font-bold text-gray-900">
                  Rs. {Number(cashier.online_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              {Number(cashier.credit_sales || 0) > 0 && (
                <div className="flex items-center justify-between py-0.5">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Receipt className="h-3.5 w-3.5 text-amber-500" />
                    <span>Credit / Udhaar Sales</span>
                  </div>
                  <span className="font-bold text-gray-900">
                    Rs. {Number(cashier.credit_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notes if any */}
          {cashier.notes && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-xs">
              <span className="font-semibold text-gray-600 block mb-0.5 text-[11px]">Cashier Handover Notes:</span>
              <p className="text-gray-800 italic">{cashier.notes}</p>
            </div>
          )}
        </div>

        {/* Footer (Compact) */}
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex-shrink-0 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>

          {!isClosed && (
            <button
              type="button"
              onClick={onAdminClose}
              disabled={actionLoading}
              className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-md shadow-amber-600/20 hover:bg-amber-700 disabled:opacity-50 transition-all"
            >
              <Lock className="h-3.5 w-3.5" />
              {actionLoading ? 'Closing...' : 'Close Shift on Behalf'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
